import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * ImpersonationBanner — Salt görüntüleme modunda tüm sayfaların üstünde sticky uyarı.
 * Kullanıcı "impersonator_token" localStorage'da varsa aktif sayılır.
 * "Geri Dön" butonu impersonation'ı kapatıp orijinal admin oturumunu restore eder.
 */
const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const { user, setToken, setUser } = useAuth() || {};
  const [remaining, setRemaining] = useState('');
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Impersonation durumunu localStorage'dan oku
  useEffect(() => {
    const check = () => {
      const t = localStorage.getItem('impersonator_token');
      setIsImpersonating(!!t);
    };
    check();
    const onStorage = () => check();
    window.addEventListener('storage', onStorage);
    window.addEventListener('impersonation-changed', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('impersonation-changed', onStorage);
    };
  }, []);

  // Kalan süreyi göster (30dk limit)
  useEffect(() => {
    if (!isImpersonating) return;
    const startedStr = localStorage.getItem('impersonation_started_at');
    if (!startedStr) return;
    const started = new Date(startedStr).getTime();
    const timer = setInterval(() => {
      const expireAt = started + 30 * 60 * 1000;
      const diff = expireAt - Date.now();
      if (diff <= 0) {
        handleStop(true);
      } else {
        const mm = Math.floor(diff / 60000);
        const ss = Math.floor((diff % 60000) / 1000);
        setRemaining(`${mm}:${ss.toString().padStart(2, '0')}`);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isImpersonating]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStop = async (silent = false) => {
    try {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        await axios.post(
          `${API}/admin/impersonate/stop`,
          {},
          { headers: { Authorization: `Bearer ${currentToken}` } }
        ).catch(() => {});
      }
    } finally {
      // Orijinal admin token'ına dön
      const impToken = localStorage.getItem('impersonator_token');
      const impUser = localStorage.getItem('impersonator_user');
      localStorage.removeItem('impersonator_token');
      localStorage.removeItem('impersonator_user');
      localStorage.removeItem('impersonation_started_at');
      localStorage.removeItem('impersonation_id');
      if (impToken) {
        localStorage.setItem('token', impToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${impToken}`;
        if (impUser) {
          try {
            const u = JSON.parse(impUser);
            localStorage.setItem('userName', u.name || u.email || '');
            localStorage.setItem('email', u.email || '');
            localStorage.setItem('firma', u.firma || '');
            localStorage.setItem('userId', u.id || '');
            if (setUser) setUser(u);
          } catch (_) { /* ignore */ }
        }
        if (setToken) setToken(impToken);
      }
      window.dispatchEvent(new Event('impersonation-changed'));
      setIsImpersonating(false);
      if (!silent) toast.success('Kendi hesabınıza geri döndünüz.');
      // Müşteri yönetimine dönsün — hard reload ile tüm state temizlensin
      setTimeout(() => { window.location.href = '/panel/panel-musterileri'; }, 200);
    }
  };

  if (!isImpersonating) return null;

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[9999] w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 border-b-4 border-red-600 shadow-lg"
    >
      <div className="flex items-center justify-between px-3 py-2 gap-2 text-white max-w-[1920px] mx-auto">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-white/20 ring-1 ring-white/40">
            <Eye className="w-4 h-4" />
          </div>
          <div className="text-[13px] font-semibold tracking-wide truncate">
            MÜŞTERİ OLARAK GÖZAT — <span className="font-bold underline">{user?.email || user?.name || 'müşteri'}</span> kullanıcısı olarak görüntülüyorsunuz
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] bg-black/25 px-2 py-0.5 rounded-full">
            <AlertCircle className="w-3 h-3" />
            Test modu — sepet/sipariş aktif · yalnızca şifre değiştirme engelli
          </span>
          {remaining && (
            <span className="hidden md:inline-flex items-center text-[11px] font-mono bg-white/20 px-2 py-0.5 rounded">
              ⏱ {remaining}
            </span>
          )}
        </div>
        <button
          onClick={() => handleStop(false)}
          data-testid="impersonation-stop-btn"
          className="flex items-center gap-1.5 bg-white text-amber-700 hover:bg-amber-50 px-3 py-1.5 rounded font-bold text-xs shadow transition"
        >
          <X className="w-4 h-4" /> Geri Dön
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
